import { isValid, parseISO } from 'date-fns';
import { enUS } from 'date-fns/locale';

export type Frequency = 'daily' | 'weekly' | 'monthly';
export type MonthlyRepeat = 'dayOfMonth' | 'weekdayOfMonth';

export interface ScheduleForm {
  frequency: Frequency;
  interval: number;
  // 0 = Monday … 6 = Sunday
  weekday: number;
  monthlyRepeat: MonthlyRepeat;
  // null follows the start date until the user picks one.
  dayOfMonth: number | null;
  monthWeek: number | null;
  startDate: string;
}

export type ParsedScheduleRule = Omit<ScheduleForm, 'startDate'>;

export const NEW_SCHEDULE_RULE: ParsedScheduleRule = {
  frequency: 'weekly',
  interval: 1,
  weekday: 0,
  monthlyRepeat: 'dayOfMonth',
  dayOfMonth: null,
  monthWeek: null
};

// ice_cube reads -1 as the last day, or the last such weekday, of the month.
export const LAST = -1;

const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const WEEKDAYS = WEEKDAY_NAMES.slice(0, 5).map((label, value) => ({ label, value }));
export const WEEKS_OF_MONTH = [1, 2, 3, 4, LAST];
export const DAYS_SOME_MONTHS_LACK = [29, 30, 31];

export const weekdayName = (weekday: number): string => WEEKDAY_NAMES[weekday] ?? '';

// IceCube day mapping: our 0=Mon...6=Sun → IceCube 1=Mon...0=Sun
const toIceCubeDay = (weekday: number) => (weekday + 1) % 7;
const fromIceCubeDay = (iceCubeDay: number) => (iceCubeDay + 6) % 7;

export const ordinal = (n: number): string => enUS.localize.ordinalNumber(n);

export const weekOfMonthLabel = (week: number): string => (week === LAST ? 'Last' : ordinal(week));
export const dayOfMonthLabel = (day: number): string => (day === LAST ? 'Last day of month' : ordinal(day));

// The date input lets a year run past four digits, which parses to Invalid Date and makes format throw.
export const isCalendarDate = (value: string): boolean => !!value && isValid(parseISO(value));

// parseISO reads the date as a local calendar day, which is the day the user picked.
const startDay = (startDate: string): number =>
  (isCalendarDate(startDate) ? parseISO(startDate) : new Date()).getDate();

export const resolvedDayOfMonth = (form: ScheduleForm): number => form.dayOfMonth ?? startDay(form.startDate);

// Days 29–31 always fall in the month's last week, so their weekday is that month's last one.
export const resolvedMonthWeek = (form: ScheduleForm): number => {
  if (form.monthWeek !== null) return form.monthWeek;
  const week = Math.ceil(startDay(form.startDate) / 7);
  return week > 4 ? LAST : week;
};

const monthlyValidations = (form: ScheduleForm): Record<string, unknown> =>
  form.monthlyRepeat === 'dayOfMonth'
    ? { day_of_month: [resolvedDayOfMonth(form)] }
    : { day_of_week: { [toIceCubeDay(form.weekday)]: [resolvedMonthWeek(form)] } };

export const buildScheduleRule = (form: ScheduleForm): Record<string, unknown> => {
  const { frequency, interval, weekday, startDate } = form;
  // UTC midnight of the chosen day on purpose: the api evaluates schedules in UTC.
  const startTime = startDate ? new Date(startDate).toISOString() : new Date().toISOString();

  const ruleByFrequency: Record<Frequency, () => Record<string, unknown>> = {
    daily: () => ({ validations: {}, rule_type: 'IceCube::DailyRule', interval }),
    weekly: () => ({ validations: { day: [toIceCubeDay(weekday)] }, rule_type: 'IceCube::WeeklyRule', interval }),
    monthly: () => ({ validations: monthlyValidations(form), rule_type: 'IceCube::MonthlyRule', interval })
  };

  return {
    start_time: startTime,
    end_time: null,
    duration: 0,
    interval_seconds: 0,
    rrules: [ruleByFrequency[frequency]()],
    rtimes: [],
    extimes: []
  };
};

const parseMonthly = (validations: Record<string, unknown>): Partial<ParsedScheduleRule> => {
  const dayOfWeek = validations.day_of_week as Record<string, number[]> | undefined;
  const [iceCubeDay, weeks] = Object.entries(dayOfWeek || {})[0] || [];
  if (iceCubeDay !== undefined) {
    return {
      monthlyRepeat: 'weekdayOfMonth',
      weekday: fromIceCubeDay(parseInt(iceCubeDay, 10)),
      monthWeek: weeks?.[0] ?? null
    };
  }
  const days = validations.day_of_month as number[] | undefined;
  return { monthlyRepeat: 'dayOfMonth', dayOfMonth: days?.[0] ?? null };
};

export const parseScheduleRule = (rule: Record<string, unknown>): ParsedScheduleRule => {
  const rrules = (rule.rrules as Record<string, unknown>[]) || [];
  const rrule = rrules[0] || {};
  const ruleType = rrule.rule_type as string;
  const validations = (rrule.validations as Record<string, unknown>) || {};
  const parsed: ParsedScheduleRule = {
    ...NEW_SCHEDULE_RULE,
    frequency: 'daily',
    interval: (rrule.interval as number) || 1
  };

  if (ruleType?.includes('Weekly')) {
    const days = (validations.day as number[]) || [1];
    return { ...parsed, frequency: 'weekly', weekday: fromIceCubeDay(days[0]) };
  }
  if (ruleType?.includes('Monthly')) return { ...parsed, frequency: 'monthly', ...parseMonthly(validations) };
  return parsed;
};

const every = (interval: number, unit: string, once: string) => (interval === 1 ? once : `Every ${interval} ${unit}s`);

const monthlyTarget = (form: ScheduleForm): string => {
  if (form.monthlyRepeat === 'weekdayOfMonth') {
    const week = resolvedMonthWeek(form);
    return `${week === LAST ? 'last' : ordinal(week)} ${weekdayName(form.weekday)}`;
  }
  const day = resolvedDayOfMonth(form);
  return day === LAST ? 'last day of the month' : ordinal(day);
};

// Plain words for the cadence, e.g. "Monthly on the 29th" or "Every 2 weeks on Monday".
export const describeSchedule = (form: ScheduleForm): string => {
  const { frequency, interval } = form;
  if (frequency === 'daily') return every(interval, 'day', 'Daily');
  if (frequency === 'weekly') return `${every(interval, 'week', 'Weekly')} on ${weekdayName(form.weekday)}`;
  return `${every(interval, 'month', 'Monthly')} on the ${monthlyTarget(form)}`;
};
